import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../api/client.js';
import type { Comment, WorkOrderDetail } from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS } from '../../shared/permissions.js';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  InternalBadge,
  Spinner,
  TextArea,
  classes,
} from '../components/ui.js';
import { formatDateTime } from '../lib/format.js';

export function CommentsSection({ workOrder }: { workOrder: WorkOrderDetail }) {
  const queryClient = useQueryClient();
  const { can, isContractor, user } = useSession();
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mayPostInternal = isContractor && can(PERMISSIONS.COMMENT_CREATE_INTERNAL);
  const mayPost =
    (can(PERMISSIONS.COMMENT_CREATE_CUSTOMER) || mayPostInternal) && workOrder.status !== 'closed';

  const { data, isLoading } = useQuery({
    queryKey: ['comments', workOrder.id],
    queryFn: () =>
      apiRequest<{ comments: Comment[] }>(`/api/work-orders/${workOrder.id}/comments`).then(
        (result) => result.comments,
      ),
  });

  const post = useMutation({
    mutationFn: () =>
      apiRequest(`/api/work-orders/${workOrder.id}/comments`, {
        method: 'POST',
        body: {
          body: body.trim(),
          visibility: internal ? 'internal' : 'customer_visible',
        },
      }),
    onSuccess: () => {
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['comments', workOrder.id] });
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Your comment could not be posted. Please try again.',
      );
    },
  });

  const remove = useMutation({
    mutationFn: (commentId: string) =>
      apiRequest(`/api/comments/${commentId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['comments', workOrder.id] });
    },
  });

  return (
    <Card title="Comments">
      <div className="flex flex-col gap-4">
        {error && <Alert variant="error">{error}</Alert>}

        {mayPost && (
          <div className="flex flex-col gap-2">
            <TextArea
              aria-label="Add a comment"
              rows={3}
              placeholder={internal ? 'Internal note — not visible to USPS' : 'Add a comment…'}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              className={internal ? 'bg-amber-50' : undefined}
            />
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => {
                  setError(null);
                  post.mutate();
                }}
                disabled={body.trim().length < 2}
                loading={post.isPending}
              >
                Post comment
              </Button>
              {mayPostInternal && (
                <label className="flex items-center gap-1.5 text-sm text-slate-800">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-600"
                    checked={internal}
                    onChange={(event) => setInternal(event.target.checked)}
                  />
                  VGS internal only
                </label>
              )}
            </div>
          </div>
        )}

        {isLoading ? (
          <Spinner label="Loading comments" />
        ) : (data ?? []).length === 0 ? (
          <EmptyState title="No comments yet" description="Comments appear here in order." />
        ) : (
          <ul className="flex flex-col gap-3">
            {(data ?? []).map((comment) => (
              <li
                key={comment.id}
                className={classes(
                  'rounded-md p-3 ring-1',
                  comment.visibility === 'internal'
                    ? 'bg-amber-50 ring-amber-200'
                    : 'bg-slate-50 ring-slate-200',
                )}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-slate-900">{comment.authorName}</span>
                  <span className="text-xs text-slate-600">
                    {comment.authorOrgName} · {formatDateTime(comment.createdAt)}
                  </span>
                  {comment.visibility === 'internal' && <InternalBadge />}
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{comment.body}</p>
                {comment.authorUserId === user?.userId && (
                  <Button
                    variant="ghost"
                    onClick={() => remove.mutate(comment.id)}
                    loading={remove.isPending}
                  >
                    Delete
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
